import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useAuth } from '../contexts/AuthContext';
import authenticatedFetch from '../utils/authenticatedFetch';
import './EquipmentDeviceDetails.css';

interface EquipmentDevice {
  deviceId: string;
  enterpriseId: string;
  storeId: string;
  storeName: string;
  manufacturer: string;
  equipmentType: string;
  modelNumber: string;
  serialNumber: string;
  installDate: string;
  warrantyExpiration: string;
  status: 'active' | 'inactive' | 'maintenance' | 'decommissioned';
  connectionStatus: 'online' | 'offline' | 'error';
  healthScore: number;
  lastSeen: string | null;
  lastMetricsUpdate: string | null;
  location: {
    area: string;
    position: string;
  };
  metadata?: any;
  healthMetrics?: {
    uptimePercentage: number;
    avgResponseTime: number;
    errorRate: number;
    maintenanceCompliance: number;
  };
}

interface MaintenanceNote {
  noteId: string;
  deviceId: string;
  timestamp: string;
  author: string;
  noteType: 'maintenance' | 'repair' | 'inspection' | 'calibration' | 'cleaning';
  title: string;
  description: string;
  partsReplaced?: string[];
  cost?: number;
  nextServiceDate?: string;
}

interface Alert {
  alertId: string;
  deviceId: string;
  timestamp: string;
  severity: 'critical' | 'warning' | 'info';
  alertType: string;
  message: string;
  status: 'active' | 'acknowledged' | 'resolved';
}

const EquipmentDeviceDetails: React.FC = () => {
  const { deviceId, enterpriseId } = useParams<{ deviceId: string; enterpriseId: string }>();
  const navigate = useNavigate();
  const { session } = useAuth();

  const [device, setDevice] = useState<EquipmentDevice | null>(null);
  const [maintenanceNotes, setMaintenanceNotes] = useState<MaintenanceNote[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [metrics, setMetrics] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'maintenance' | 'alerts' | 'metrics' | 'airquality' | 'settings'>('overview');
  const [airQualityData, setAirQualityData] = useState<any>(null);
  const [airQualityLoading, setAirQualityLoading] = useState(false);

  // New maintenance note form
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [newNote, setNewNote] = useState({
    noteType: 'maintenance' as MaintenanceNote['noteType'],
    title: '',
    description: '',
    partsReplaced: '',
    cost: '',
    nextServiceDate: ''
  });

  useEffect(() => {
    if (deviceId) {
      fetchDeviceDetails();
      fetchMaintenanceNotes();
      fetchAlerts();
      fetchMetrics();
    }
  }, [deviceId]);

  // Auto-switch to air quality tab and fetch data for sensusair devices
  useEffect(() => {
    if (device?.manufacturer === 'sensusair') {
      setActiveTab('airquality');
      fetchAirQualityReadings();
    }
  }, [device?.manufacturer]);

  const fetchAirQualityReadings = async () => {
    if (!deviceId || !enterpriseId) return;
    setAirQualityLoading(true);
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.equipmentSensusairReadings(deviceId, enterpriseId)
      );
      if (response.ok) {
        const data = await response.json();
        setAirQualityData(data.success ? data.data : data);
      }
    } catch (err) {
      console.error('Error fetching air quality readings:', err);
    } finally {
      setAirQualityLoading(false);
    }
  };

  const fetchDeviceDetails = async () => {
    try {
      setLoading(true);
      const url = `${API_ENDPOINTS.equipmentDevice(deviceId!)}?enterpriseId=${enterpriseId}`;
      const response = await authenticatedFetch(url);

      if (response.ok) {
        const data = await response.json();
        setDevice(data.success ? data.data : data);
      } else {
        throw new Error('Failed to fetch device details');
      }
    } catch (err) {
      console.error('Error fetching device:', err);
      setError(err instanceof Error ? err.message : 'Failed to load device');
    } finally {
      setLoading(false);
    }
  };

  const fetchMaintenanceNotes = async () => {
    // Mock data for now - will be replaced with API call
    const mockNotes: MaintenanceNote[] = [
      {
        noteId: '1',
        deviceId: deviceId!,
        timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        author: 'John Smith',
        noteType: 'maintenance',
        title: 'Routine Maintenance',
        description: 'Performed routine cleaning and calibration. All systems operating normally.',
        cost: 150
      },
      {
        noteId: '2',
        deviceId: deviceId!,
        timestamp: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        author: 'Sarah Johnson',
        noteType: 'repair',
        title: 'Replaced Temperature Sensor',
        description: 'Temperature sensor was reading incorrectly. Replaced with new OEM part.',
        partsReplaced: ['Temperature Sensor TS-2000'],
        cost: 450,
        nextServiceDate: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString()
      }
    ];
    setMaintenanceNotes(mockNotes);
  };

  const fetchAlerts = async () => {
    try {
      const response = await authenticatedFetch(
        API_ENDPOINTS.equipmentAlerts({ deviceId })
      );

      if (response.ok) {
        const data = await response.json();
        setAlerts(data.success ? data.data.alerts : data.alerts || []);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  };

  const fetchMetrics = async () => {
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINTS.equipmentMetrics(deviceId!)}?days=30`
      );

      if (response.ok) {
        const data = await response.json();
        setMetrics(data.success ? data.data.metrics : data.metrics || []);
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
      setMetrics([]);
    }
  };

  const handleAddMaintenanceNote = async () => {
    const note: MaintenanceNote = {
      noteId: Date.now().toString(),
      deviceId: deviceId!,
      timestamp: new Date().toISOString(),
      author: session?.getIdToken().payload?.email || 'Unknown',
      noteType: newNote.noteType,
      title: newNote.title,
      description: newNote.description,
      partsReplaced: newNote.partsReplaced ? newNote.partsReplaced.split(',').map(p => p.trim()) : undefined,
      cost: newNote.cost ? parseFloat(newNote.cost) : undefined,
      nextServiceDate: newNote.nextServiceDate || undefined
    };

    // Add to list immediately (optimistic update)
    setMaintenanceNotes([note, ...maintenanceNotes]);
    setShowNoteForm(false);
    setNewNote({
      noteType: 'maintenance',
      title: '',
      description: '',
      partsReplaced: '',
      cost: '',
      nextServiceDate: ''
    });

    // TODO: Save to backend
    console.log('Saving maintenance note:', note);
  };

  const getHealthColor = (score: number): string => {
    if (score >= 80) return '#28a745';
    if (score >= 60) return '#ffc107';
    if (score >= 40) return '#fd7e14';
    return '#dc3545';
  };

  const getAqColor = (score: number | null | undefined): string => {
    if (score == null) return '#6c757d';
    if (score >= 80) return '#28a745';
    if (score >= 50) return '#ffc107';
    return '#dc3545';
  };

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return '#dc3545';
      case 'warning': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  if (loading) {
    return (
      <div className="equipment-device-details">
        <div className="loading-spinner">Loading device details...</div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="equipment-device-details">
        <div className="error-message">
          <h3>Error</h3>
          <p>{error || 'Device not found'}</p>
          <button onClick={() => navigate('/equipment')} className="btn-modern-secondary">
            Back to Equipment List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="equipment-device-details">
      {/* Header */}
      <div className="device-header">
        <button onClick={() => navigate('/equipment')} className="back-button">
          ← Back to Equipment List
        </button>
        <div className="device-title">
          <h1>
            {device.manufacturer === 'sensusair' ? 'SensusAir' : device.manufacturer.toUpperCase()}{' '}
            {device.equipmentType === 'air-quality-sensor' ? 'Air Quality Sensor' : device.equipmentType.replace('-', ' ').toUpperCase()}
          </h1>
          <p className="device-subtitle">{device.storeName} - {device.location.area}</p>
        </div>
        <div className="device-status-badges">
          <span className={`status-badge ${device.connectionStatus}`}>
            {device.connectionStatus.toUpperCase()}
          </span>
          <span className={`status-badge ${device.status}`}>
            {device.status.toUpperCase()}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'maintenance' ? 'active' : ''}
          onClick={() => setActiveTab('maintenance')}
        >
          Maintenance
        </button>
        <button
          className={activeTab === 'alerts' ? 'active' : ''}
          onClick={() => setActiveTab('alerts')}
        >
          Alerts {alerts && alerts.filter(a => a.status === 'active').length > 0 && `(${alerts.filter(a => a.status === 'active').length})`}
        </button>
        {device.manufacturer === 'sensusair' && (
          <button
            className={activeTab === 'airquality' ? 'active' : ''}
            onClick={() => setActiveTab('airquality')}
          >
            Air Quality
          </button>
        )}
        <button
          className={activeTab === 'metrics' ? 'active' : ''}
          onClick={() => setActiveTab('metrics')}
        >
          Metrics
        </button>
        <button
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="info-grid">
              <div className="info-card">
                <h3>Device Information</h3>
                <div className="info-row">
                  <span className="label">Model:</span>
                  <span className="value">{device.modelNumber}</span>
                </div>
                <div className="info-row">
                  <span className="label">Serial Number:</span>
                  <span className="value">{device.serialNumber}</span>
                </div>
                <div className="info-row">
                  <span className="label">Location:</span>
                  <span className="value">{device.location.area} - {device.location.position}</span>
                </div>
                <div className="info-row">
                  <span className="label">Install Date:</span>
                  <span className="value">{new Date(device.installDate).toLocaleDateString()}</span>
                </div>
                <div className="info-row">
                  <span className="label">Warranty Expires:</span>
                  <span className="value">{new Date(device.warrantyExpiration).toLocaleDateString()}</span>
                </div>
                {device.lastSeen && (
                  <div className="info-row">
                    <span className="label">Last Seen:</span>
                    <span className="value">{new Date(device.lastSeen).toLocaleString()}</span>
                  </div>
                )}
              </div>

              <div className="info-card">
                <h3>Health & Performance</h3>
                <div className="health-score">
                  <div className="score-circle" style={{ borderColor: getHealthColor(device.healthScore) }}>
                    <span className="score-value" style={{ color: getHealthColor(device.healthScore) }}>
                      {device.healthScore}
                    </span>
                    <span className="score-label">Health Score</span>
                  </div>
                </div>
                {device.healthMetrics && (
                  <div className="metrics-list">
                    <div className="metric-item">
                      <span className="metric-label">Uptime:</span>
                      <span className="metric-value">{device.healthMetrics.uptimePercentage}%</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Avg Response:</span>
                      <span className="metric-value">{device.healthMetrics.avgResponseTime}ms</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Error Rate:</span>
                      <span className="metric-value">{device.healthMetrics.errorRate}%</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Maintenance Compliance:</span>
                      <span className="metric-value">{device.healthMetrics.maintenanceCompliance}%</span>
                    </div>
                  </div>
                )}
              </div>

              {device.metadata && (
                <div className="info-card">
                  <h3>Current Status</h3>
                  <div className="info-row">
                    <span className="label">Operating Mode:</span>
                    <span className="value">{device.metadata.operatingMode || 'Unknown'}</span>
                  </div>
                  {device.metadata.chamberTemperature && (
                    <div className="info-row">
                      <span className="label">Chamber Temperature:</span>
                      <span className="value">{device.metadata.chamberTemperature}°F</span>
                    </div>
                  )}
                  {device.metadata.chamberHumidity && (
                    <div className="info-row">
                      <span className="label">Chamber Humidity:</span>
                      <span className="value">{device.metadata.chamberHumidity}%</span>
                    </div>
                  )}
                  {device.metadata.wifiSignalStrength && (
                    <div className="info-row">
                      <span className="label">WiFi Signal:</span>
                      <span className="value">{device.metadata.wifiSignalStrength}%</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="maintenance-tab">
            <div className="tab-header">
              <h2>Maintenance History</h2>
              <button
                onClick={() => setShowNoteForm(!showNoteForm)}
                className="btn-modern-primary"
              >
                {showNoteForm ? 'Cancel' : 'Add Maintenance Note'}
              </button>
            </div>

            {showNoteForm && (
              <div className="maintenance-note-form">
                <h3>New Maintenance Note</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Note Type</label>
                    <select
                      value={newNote.noteType}
                      onChange={(e) => setNewNote({ ...newNote, noteType: e.target.value as MaintenanceNote['noteType'] })}
                    >
                      <option value="maintenance">Routine Maintenance</option>
                      <option value="repair">Repair</option>
                      <option value="inspection">Inspection</option>
                      <option value="calibration">Calibration</option>
                      <option value="cleaning">Deep Cleaning</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={newNote.title}
                      onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                      placeholder="Brief description of work performed"
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea
                      value={newNote.description}
                      onChange={(e) => setNewNote({ ...newNote, description: e.target.value })}
                      placeholder="Detailed notes about the work performed"
                      rows={4}
                    />
                  </div>
                  <div className="form-group">
                    <label>Parts Replaced (comma-separated)</label>
                    <input
                      type="text"
                      value={newNote.partsReplaced}
                      onChange={(e) => setNewNote({ ...newNote, partsReplaced: e.target.value })}
                      placeholder="Part 1, Part 2, Part 3"
                    />
                  </div>
                  <div className="form-group">
                    <label>Cost ($)</label>
                    <input
                      type="number"
                      value={newNote.cost}
                      onChange={(e) => setNewNote({ ...newNote, cost: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                  <div className="form-group">
                    <label>Next Service Date</label>
                    <input
                      type="date"
                      value={newNote.nextServiceDate}
                      onChange={(e) => setNewNote({ ...newNote, nextServiceDate: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button onClick={() => setShowNoteForm(false)} className="btn-modern-secondary">
                    Cancel
                  </button>
                  <button
                    onClick={handleAddMaintenanceNote}
                    className="btn-modern-primary"
                    disabled={!newNote.title || !newNote.description}
                  >
                    Save Note
                  </button>
                </div>
              </div>
            )}

            <div className="maintenance-timeline">
              {maintenanceNotes.length === 0 ? (
                <p className="no-data">No maintenance records found. Add your first note above.</p>
              ) : (
                maintenanceNotes.map(note => (
                  <div key={note.noteId} className="maintenance-note">
                    <div className="note-header">
                      <span className={`note-type ${note.noteType}`}>{note.noteType.toUpperCase()}</span>
                      <span className="note-date">{new Date(note.timestamp).toLocaleDateString()}</span>
                    </div>
                    <h4>{note.title}</h4>
                    <p>{note.description}</p>
                    <div className="note-details">
                      <span className="note-author">By: {note.author}</span>
                      {note.cost && <span className="note-cost">Cost: ${note.cost.toFixed(2)}</span>}
                    </div>
                    {note.partsReplaced && note.partsReplaced.length > 0 && (
                      <div className="parts-replaced">
                        <strong>Parts Replaced:</strong>
                        <ul>
                          {note.partsReplaced.map((part, idx) => (
                            <li key={idx}>{part}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {note.nextServiceDate && (
                      <div className="next-service">
                        Next Service: {new Date(note.nextServiceDate).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div className="alerts-tab">
            <h2>Device Alerts</h2>
            {!alerts || alerts.length === 0 ? (
              <p className="no-data">No alerts for this device.</p>
            ) : (
              <div className="alerts-list">
                {alerts.map(alert => (
                  <div key={alert.alertId} className={`alert-card ${alert.severity}`}>
                    <div className="alert-header">
                      <span className="alert-severity" style={{ backgroundColor: getSeverityColor(alert.severity) }}>
                        {alert.severity.toUpperCase()}
                      </span>
                      <span className="alert-time">{new Date(alert.timestamp).toLocaleString()}</span>
                    </div>
                    <h4>{alert.alertType}</h4>
                    <p>{alert.message}</p>
                    <div className="alert-status">
                      Status: <span className={`badge ${alert.status}`}>{alert.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'airquality' && device.manufacturer === 'sensusair' && (
          <div className="airquality-tab">
            <div className="tab-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>Air Quality Readings</h2>
              <button
                onClick={fetchAirQualityReadings}
                className="btn-modern-secondary"
                disabled={airQualityLoading}
                style={{ fontSize: '13px', padding: '8px 16px' }}
              >
                {airQualityLoading ? 'Refreshing...' : 'Refresh Readings'}
              </button>
            </div>

            {airQualityLoading && !airQualityData ? (
              <p>Loading air quality data...</p>
            ) : airQualityData ? (
              <div className="info-grid">
                {/* CO2 */}
                <div className="info-card">
                  <h3>CO2 Level</h3>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: getAqColor(airQualityData.scores?.co2) }}>
                      {airQualityData.readings?.co2 != null ? airQualityData.readings.co2 : '--'}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>ppm</div>
                    <div style={{ marginTop: '8px' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px', borderRadius: '12px',
                        fontSize: '13px', fontWeight: 'bold', color: '#fff',
                        backgroundColor: getAqColor(airQualityData.scores?.co2)
                      }}>
                        Score: {airQualityData.scores?.co2 != null ? Math.round(airQualityData.scores.co2) : '--'}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', textAlign: 'center' }}>
                    Good: &lt;800 | Fair: 800-1200 | Poor: &gt;1200
                  </div>
                </div>

                {/* Temperature */}
                <div className="info-card">
                  <h3>Temperature</h3>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: getAqColor(airQualityData.scores?.temperature) }}>
                      {airQualityData.readings?.temperature != null
                        ? `${(airQualityData.readings.temperature * 9/5 + 32).toFixed(1)}`
                        : '--'
                      }
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      F ({airQualityData.readings?.temperature != null ? `${airQualityData.readings.temperature.toFixed(1)} C` : '--'})
                    </div>
                    <div style={{ marginTop: '8px' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px', borderRadius: '12px',
                        fontSize: '13px', fontWeight: 'bold', color: '#fff',
                        backgroundColor: getAqColor(airQualityData.scores?.temperature)
                      }}>
                        Score: {airQualityData.scores?.temperature != null ? Math.round(airQualityData.scores.temperature) : '--'}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', textAlign: 'center' }}>
                    Good: 68-75F | Fair: 64-68 or 75-79F | Poor: &lt;64 or &gt;79F
                  </div>
                </div>

                {/* Humidity */}
                <div className="info-card">
                  <h3>Humidity</h3>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: getAqColor(airQualityData.scores?.humidity) }}>
                      {airQualityData.readings?.humidity != null ? airQualityData.readings.humidity : '--'}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>% RH</div>
                    <div style={{ marginTop: '8px' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px', borderRadius: '12px',
                        fontSize: '13px', fontWeight: 'bold', color: '#fff',
                        backgroundColor: getAqColor(airQualityData.scores?.humidity)
                      }}>
                        Score: {airQualityData.scores?.humidity != null ? Math.round(airQualityData.scores.humidity) : '--'}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', textAlign: 'center' }}>
                    Good: 40-60% | Fair: 30-40 or 60-70% | Poor: &lt;30 or &gt;70%
                  </div>
                </div>

                {/* PM2.5 */}
                <div className="info-card">
                  <h3>PM2.5</h3>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div style={{ fontSize: '36px', fontWeight: 'bold', color: getAqColor(airQualityData.scores?.pm25) }}>
                      {airQualityData.readings?.pm25 != null ? airQualityData.readings.pm25 : '--'}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>ug/m3</div>
                    <div style={{ marginTop: '8px' }}>
                      <span style={{
                        display: 'inline-block', padding: '4px 12px', borderRadius: '12px',
                        fontSize: '13px', fontWeight: 'bold', color: '#fff',
                        backgroundColor: getAqColor(airQualityData.scores?.pm25)
                      }}>
                        Score: {airQualityData.scores?.pm25 != null ? Math.round(airQualityData.scores.pm25) : '--'}
                      </span>
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#6c757d', textAlign: 'center' }}>
                    Good: &lt;12 | Fair: 12-35 | Poor: &gt;35
                  </div>
                </div>

                {/* VOC */}
                {airQualityData.readings?.voc != null && (
                  <div className="info-card">
                    <h3>VOC</h3>
                    <div style={{ textAlign: 'center', padding: '16px 0' }}>
                      <div style={{ fontSize: '36px', fontWeight: 'bold' }}>
                        {airQualityData.readings.voc}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6c757d' }}>ppb</div>
                    </div>
                  </div>
                )}

                {/* Overall Score */}
                <div className="info-card">
                  <h3>Overall Air Quality</h3>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <div className="score-circle" style={{ borderColor: getHealthColor(airQualityData.healthScore || 0), margin: '0 auto' }}>
                      <span className="score-value" style={{ color: getHealthColor(airQualityData.healthScore || 0) }}>
                        {airQualityData.healthScore || '--'}
                      </span>
                      <span className="score-label">Health Score</span>
                    </div>
                    <p style={{ fontSize: '13px', color: '#6c757d', marginTop: '12px' }}>
                      Weighted: CO2 35% | PM2.5 30% | Temp 20% | Humidity 15%
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <p className="no-data">No air quality data available. Try syncing from the dashboard.</p>
            )}

            {airQualityData?.timestamp && (
              <p style={{ fontSize: '12px', color: '#6c757d', marginTop: '16px', textAlign: 'right' }}>
                Last reading: {new Date(airQualityData.timestamp).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {activeTab === 'metrics' && (
          <div className="metrics-tab">
            <h2>Performance Metrics (Last 30 Days)</h2>
            {!metrics || metrics.length === 0 ? (
              <p className="no-data">No metrics data available for this device.</p>
            ) : (
              <div className="info-grid">
                <div className="info-card">
                  <h3>Health Score Trend</h3>
                  <div className="metric-summary">
                    <div className="metric-item">
                      <span className="metric-label">Average:</span>
                      <span className="metric-value">{Math.round(metrics.reduce((sum, m) => sum + m.metrics.healthScore, 0) / metrics.length)}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Highest:</span>
                      <span className="metric-value">{Math.max(...metrics.map(m => m.metrics.healthScore))}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Lowest:</span>
                      <span className="metric-value">{Math.min(...metrics.map(m => m.metrics.healthScore))}</span>
                    </div>
                  </div>
                </div>

                <div className="info-card">
                  <h3>Uptime</h3>
                  <div className="metric-summary">
                    <div className="metric-item">
                      <span className="metric-label">Average Uptime:</span>
                      <span className="metric-value">{Math.round(metrics.reduce((sum, m) => sum + m.metrics.uptimePercentage, 0) / metrics.length)}%</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Avg Response Time:</span>
                      <span className="metric-value">{Math.round(metrics.reduce((sum, m) => sum + m.metrics.avgResponseTime, 0) / metrics.length)}ms</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Avg Error Rate:</span>
                      <span className="metric-value">{(metrics.reduce((sum, m) => sum + m.metrics.errorRate, 0) / metrics.length).toFixed(1)}%</span>
                    </div>
                  </div>
                </div>

                <div className="info-card">
                  <h3>Cook Cycles</h3>
                  <div className="metric-summary">
                    <div className="metric-item">
                      <span className="metric-label">Total Cycles:</span>
                      <span className="metric-value">{metrics.reduce((sum, m) => sum + m.metrics.cookCycles, 0)}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Avg per Day:</span>
                      <span className="metric-value">{Math.round(metrics.reduce((sum, m) => sum + m.metrics.cookCycles, 0) / metrics.length)}</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Avg Temp:</span>
                      <span className="metric-value">{Math.round(metrics.reduce((sum, m) => sum + m.metrics.chamberTempAvg, 0) / metrics.length)}°F</span>
                    </div>
                  </div>
                </div>

                <div className="info-card">
                  <h3>Energy & Efficiency</h3>
                  <div className="metric-summary">
                    <div className="metric-item">
                      <span className="metric-label">Total Energy:</span>
                      <span className="metric-value">{metrics.reduce((sum, m) => sum + m.metrics.energyUsage, 0)} kWh</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Avg Efficiency:</span>
                      <span className="metric-value">{Math.round(metrics.reduce((sum, m) => sum + m.metrics.efficiency, 0) / metrics.length)}%</span>
                    </div>
                    <div className="metric-item">
                      <span className="metric-label">Door Opens:</span>
                      <span className="metric-value">{metrics.reduce((sum, m) => sum + m.metrics.doorOpenEvents, 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-tab">
            <h2>Device Settings</h2>
            <div className="settings-section">
              <h3>Status Management</h3>
              <div className="form-group">
                <label>Device Status</label>
                <select value={device.status} onChange={(e) => console.log('Status change:', e.target.value)}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="maintenance">Maintenance Mode</option>
                  <option value="decommissioned">Decommissioned</option>
                </select>
              </div>
              <button className="btn-modern-primary">Update Status</button>
            </div>

            <div className="settings-section">
              <h3>Location</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Area</label>
                  <input type="text" value={device.location.area} />
                </div>
                <div className="form-group">
                  <label>Position</label>
                  <input type="text" value={device.location.position} />
                </div>
              </div>
              <button className="btn-modern-primary">Update Location</button>
            </div>

            <div className="settings-section danger-zone">
              <h3>Danger Zone</h3>
              <button className="btn-danger">Decommission Device</button>
              <button className="btn-danger">Delete Device</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EquipmentDeviceDetails;
